import React, { useState, useCallback } from 'react';
import { Search } from 'lucide-react';
import { useBatchSource, DEFAULT_BATCH_FILTERS } from '../hooks/useBatchSource';
import type { BatchSourceFilters } from '../hooks/useBatchSource';
import { FilterPanel } from '../components/BatchSource/FilterPanel';
import { ResultsTable } from '../components/BatchSource/ResultsTable';
import { Breadcrumbs } from '../components/Layout/Breadcrumbs';
import { useMarketStore } from '../store/useMarketStore';

export const BatchSourcePage: React.FC = () => {
  const [filters, setFiltersState] = useState<BatchSourceFilters>({ ...DEFAULT_BATCH_FILTERS });
  const activeMarket = useMarketStore((s) => s.activeMarket);

  const setFilters = useCallback((updates: Partial<BatchSourceFilters>) => {
    setFiltersState((prev) => ({ ...prev, ...updates }));
  }, []);

  const { leads, total, page, pages, isLoading, isFetching, error } = useBatchSource(filters);

  // Client-side filtering for multifamily/permit/violation (API may not support these directly)
  const filteredLeads = React.useMemo(() => {
    let result = leads;

    if (filters.multifamilyOnly) {
      result = result.filter((l) => l.is_multifamily === true || (l.total_units && l.total_units > 1));
    }

    if (filters.permitFilter === 'no_recent') {
      result = result.filter((l) => !l.has_recent_mep);
    } else if (filters.permitFilter === 'has_permits') {
      result = result.filter((l) => l.has_recent_mep || (l.mep_permit_count && l.mep_permit_count > 0));
    }

    if (filters.violationFilter === 'has_violations') {
      result = result.filter((l) => l.has_violations || (l.violation_count && l.violation_count > 0));
    } else if (filters.violationFilter === 'no_violations') {
      result = result.filter((l) => !l.has_violations && (!l.violation_count || l.violation_count === 0));
    }

    return result;
  }, [leads, filters.multifamilyOnly, filters.permitFilter, filters.violationFilter]);

  if (error) {
    return (
      <div className="flex h-full bg-slate-950 items-center justify-center p-6">
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-6 max-w-md">
          <h2 className="text-lg font-semibold text-red-400 mb-2">Error Loading Properties</h2>
          <p className="text-stone-500 mb-4">{error.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="text-gold hover:text-amber-400 text-sm font-medium transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-slate-950 relative">
      {/* Filter sidebar */}
      <aside className="w-72 flex-shrink-0 border-r border-slate-700 bg-slate-900">
        <FilterPanel
          filters={filters}
          setFilters={setFilters}
          isLoading={isLoading}
        />
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="bg-slate-900 border-b border-slate-700 px-6 py-4">
          <Breadcrumbs className="mb-3" />
          <div className="flex items-center gap-3">
            <Search className="w-6 h-6 text-gold" />
            <div>
              <h1 className="text-xl font-semibold text-ivory">Source Deals</h1>
              <p className="text-sm text-stone-500">
                {isLoading
                  ? 'Searching properties...'
                  : `${total.toLocaleString()} properties in ${activeMarket.display_name}`}
              </p>
            </div>
          </div>
        </header>

        {/* Results */}
        <div className="flex-1 overflow-hidden">
          <ResultsTable
            leads={filteredLeads}
            total={total}
            page={page}
            pages={pages}
            isLoading={isLoading}
            isFetching={isFetching}
            filters={filters}
            setFilters={setFilters}
          />
        </div>
      </main>
    </div>
  );
};

export default BatchSourcePage;
